/**
 * Wire types for the KorOS Build API contract, with Zod schemas for the
 * responses the client persists (anything written to IndexedDB is validated
 * first, so a malformed payload cannot poison the offline cache).
 *
 * Schemas are lenient where the contract allows growth: unknown fields are
 * stripped, optional arrays default to empty.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const authConfigSchema = z.object({
  authorize_url: z.url(),
  client_id: z.string().min(1),
  redirect_uri: z.string().min(1),
  response_type: z.string().default('code'),
  code_challenge_method: z.literal('S256').default('S256'),
  scopes: z.array(z.string()).default([]),
});
export type AuthConfig = z.infer<typeof authConfigSchema>;

export const userSchema = z.object({
  id: z.string(),
  name: z.string().default(''),
  email: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  avatar_url: z.string().nullable().optional(),
  is_staff: z.boolean().optional(),
  /** Granted by KorOS (`build_superuser`): may open Django admin. */
  is_superuser: z.boolean().optional(),
  /** The language the person chose on their account (en | sw | ar; "" = never chosen). Only on /me. */
  language: z.string().nullable().optional(),
});
export type User = z.infer<typeof userSchema>;

export type AuthTokenResponse = {
  access_token: string;
  expires_in: number;
  user: User;
};

// ---------------------------------------------------------------------------
// /me
// ---------------------------------------------------------------------------

export const organizationMembershipSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.string().default('company'),
  role: z.string().default(''),
  permissions: z.array(z.string()).default([]),
  /**
   * A personal organisation left behind by an abandoned set-up (no projects, no
   * other members, nothing refers to it — `organizations.dormancy`). It never
   * gives its member a hat (`playerProfile`).
   */
  is_dormant: z.boolean().optional(),
  /**
   * A personal organisation (kind `individual_owner`, the person's own or one
   * they were added to): plumbing for an individual project owner, never
   * presented as a company.
   */
  is_personal: z.boolean().optional(),
  /** It is the caller's OWN personal organisation (a spouse added to someone else's sees `is_personal` only). */
  is_own_personal: z.boolean().optional(),
  /** Party roles this organisation holds on live projects (owner, contractor, subcontractor…). */
  sides: z.array(z.string()).optional(),
  /**
   * The person's OWN personal organisation only: "owner" (an individual owner —
   * the owner hat) or "sole_trader" (a person taking subcontracts under their
   * own name: no owner hat, never a company section). Absent elsewhere.
   */
  personal_role: z.string().nullable().optional(),
});
export type OrganizationMembership = z.infer<typeof organizationMembershipSchema>;

export const projectMembershipSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().nullable().optional(),
  organization_id: z.string(),
  organization_name: z.string().optional(),
  /** The project's organisation for the person is their personal one (an individual owner). */
  organization_is_personal: z.boolean().optional(),
  /** The person's own party on the project (a /me cached before it existed has none). */
  party_id: z.string().optional(),
  /** PartyRole: owner | contractor | quantity_surveyor | consultant | subcontractor | supplier. */
  party_role: z.string().default(''),
  /** ProjectRole, e.g. project_manager, site_supervisor, owners_pm — a label, never a permission. */
  role: z.string().default(''),
  permissions: z.array(z.string()).default([]),
  status: z.string().default('active'),
  cover_url: z.string().nullable().optional(),
  /** Reached without KorOS Build access, as a sole trader (the server's exemption). */
  via_sole_trader: z.boolean().optional(),
});
export type ProjectMembership = z.infer<typeof projectMembershipSchema>;

export const meSchema = z.object({
  user: userSchema,
  entitlements: z
    .object({
      build_access: z.boolean(),
      /**
       * Projects this person works without KorOS Build access, as a sole trader
       * (their own personal organisation's active subcontractor party). Empty
       * with Build access; everything else still needs it.
       */
      sole_trader_projects: z.array(z.string()).optional().catch([]),
    })
    .catchall(z.unknown()),
  /**
   * Build's own plans: whether they are enforced, and whether this person may
   * preview the plan pages while they are not (Build staff). Absent = neither.
   */
  billing: z
    .object({
      enforced: z.boolean().catch(false),
      preview: z.boolean().catch(false),
    })
    .optional()
    .catch(undefined),
  /** The person has a claimed worker record (engagements, attendance, earnings). */
  worker: z.boolean().optional(),
  /**
   * The person's provider profile in the marketplace (site work and/or
   * independent services), or null. Drives the `provider` hat.
   */
  provider: z
    .object({
      listing_id: z.string(),
      slug: z.string().catch(''),
      listed: z.boolean().catch(false),
      public_page: z.boolean().catch(false),
      site_work: z.boolean().catch(false),
      services: z.boolean().catch(false),
    })
    .nullable()
    .optional()
    .catch(null),
  /** Person-level rights (e.g. projects.create, build.admin). */
  permissions: z.array(z.string()).default([]),
  organizations: z.array(organizationMembershipSchema).default([]),
  projects: z.array(projectMembershipSchema).default([]),
});
export type Me = z.infer<typeof meSchema>;

// ---------------------------------------------------------------------------
// Sync
// ---------------------------------------------------------------------------

/** Operations the server registers per entity type (see sync.registry). */
export type MutationOperation =
  | 'create'
  | 'update'
  | 'delete'
  | 'action'
  | 'submit'
  | 'check_item'
  | 'start'
  | 'resolve'
  | 'correct'
  // Module 4: a daily plan line removed; an action completed by its owner.
  | 'remove'
  | 'complete'
  // Module 5: a material request withdrawn by the party that raised it.
  | 'cancel';

export type PushMutation = {
  mutation_id: string;
  entity_type: string;
  entity_id: string;
  operation: MutationOperation;
  base_version: number | null;
  payload: Record<string, unknown>;
  created_at_device: string;
  project_id: string | null;
};

export type PushRequest = {
  device_id: string;
  /** Telemetry for the admin console's sync health (never used for decisions). */
  platform?: string;
  app_version?: string;
  /** Changes still waiting on this device when the batch was sent. */
  pending_count?: number;
  mutations: PushMutation[];
};

export type ConflictInfo = {
  server_version: number | null;
  server_data: Record<string, unknown> | null;
  /** Fields that clashed in a three-way merge (editable records). */
  fields?: string[];
  allowed_resolutions: string[];
};

export type PushResult = {
  mutation_id: string;
  status: 'applied' | 'duplicate' | 'conflict' | 'rejected';
  /** For `duplicate`: what the first answer was (a resent conflict stays a conflict). */
  original_status?: 'applied' | 'conflict' | 'rejected';
  entity_version?: number;
  /** The entity as the server now has it (applied). */
  data?: Record<string, unknown>;
  conflict?: ConflictInfo;
  error?: { code: string; message: string };
};

export type PushResponse = { results: PushResult[] };

export type PullChange = {
  entity_type: string;
  entity_id: string;
  op: 'upsert' | 'delete';
  version: number;
  data: Record<string, unknown> | null;
};

export type PullResponse = {
  changes: PullChange[];
  next_cursor: string | null;
  has_more: boolean;
};
